import { ReactNode, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { LocateFixedIcon, PencilIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { FormAutocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import useBrains from '@/hooks/useBrains';
import useIntents from '@/hooks/useIntents';
import { useSendCorrection } from '@/hooks/useSendCorrection';
import i18n from '@/i18n/i18n';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { trackEvent } from '@/segment/segment';
import { Intent as BrainIntent } from '@/shared/types/intent';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { AmendPayload } from '@/shared/types/tryIt';
import { slug as slugPattern } from '@/util/validation/patterns';

import { AMEND_BUTTON_CLASS, AmendedLabel, AmendWrapper } from './AmendWrapper';
import { DetailRowSpec } from './DetailsRow';
import { getIntentInfo, IntentDetails } from './IntentDetail';
import { submitCorrection } from '../../../redux/actions';
import { selectBrainId, selectTryItSessionId } from '../../../redux/selectors';
import { ConversationTurn } from '../../../types/conversation';

const useAmendPayload = (
  turn: ConversationTurn,
  task: 'standalone' | 'planner'
): AmendPayload | null => {
  const sessionId = useSelector(selectTryItSessionId);
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);

  const requestId = turn.requestId ?? turn.metadata.nodesStack?.[0]?.request_id;
  if (!requestId || !sessionId) {
    return null;
  }
  return { request_id: requestId, task, session_id: sessionId, language: brain?.language };
};

const canShowStandaloneAmend = (turn: ConversationTurn) =>
  Boolean(turn.metadata.standaloneQuestion);

/** Standalone-question amend. `footer` is the rephrased line, so Amend sits beside it. */
export const StandaloneAmend = ({
  turn,
  footer,
}: {
  turn: ConversationTurn;
  footer?: ReactNode;
}) => {
  const payload = useAmendPayload(turn, 'standalone');
  const correction = turn.corrections?.find((c) => c.task === 'standalone');

  if (!canShowStandaloneAmend(turn) || !payload) {
    return <>{footer}</>;
  }

  return (
    <AmendWrapper
      key={payload.request_id}
      flat
      footer={footer}
      amendPayload={payload}
      isAmended={Boolean(correction)}
      correctionText={correction?.correction}
      originalText={turn.metadata.standaloneQuestion}
    />
  );
};

const MarkIncorrectControl = ({
  turn,
  payload,
}: {
  turn: ConversationTurn;
  payload: AmendPayload;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sendCorrection, isPending } = useSendCorrection();
  const [amended, setAmended] = useState(
    Boolean(turn.corrections?.some((c) => c.task === 'planner' && c.correction === 'null'))
  );

  const handleClick = async () => {
    if (isPending) {
      return;
    }
    const correction = { ...payload, task: 'planner' as const, correction: 'null' };
    try {
      const result = await sendCorrection(correction);
      if (result.status === 'ok') {
        dispatch(submitCorrection(correction));
        toast.success(t('try_it.amendment_submitted'));
        setAmended(true);
      }
    } catch {
      // useSendCorrection surfaces the error via toast.
    }
  };

  if (amended) {
    return <AmendedLabel />;
  }

  return (
    <button type="button" className={AMEND_BUTTON_CLASS} onClick={handleClick}>
      {t('try_it.mark_incorrect')}
    </button>
  );
};

export const MarkIncorrect = ({ turn }: { turn: ConversationTurn }) => {
  const payload = useAmendPayload(turn, 'planner');

  if (!payload) {
    return null;
  }

  return <MarkIncorrectControl key={payload.request_id} turn={turn} payload={payload} />;
};

const intentFormSchema = yup.object({
  intent: yup
    .string()
    .required()
    .matches(slugPattern, { message: () => i18n.t('errors.invalid_intent') }),
});

type IntentForm = yup.InferType<typeof intentFormSchema>;

const IntentAmendControl = ({
  turn,
  payload,
}: {
  turn: ConversationTurn;
  payload: AmendPayload;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const { sendCorrection, isPending: isAmending } = useSendCorrection();
  const { brain } = useBrains(brainId);
  const { brain: draftBrain } = useBrains(brain?.brain_parent_id);
  const {
    intents: brainIntents,
    createNewIntent,
    updateIntentAsync,
    updateStatus,
  } = useIntents(draftBrain?.brain_id);

  const candidates = turn.request?.intents ?? [];
  const userMessage = turn.request?.text ?? '';
  const storedAmendment = turn.corrections?.find(
    (c) => c.task === 'planner' && candidates.some((i) => i.intent === c.correction)
  )?.correction;

  const [show, setShow] = useState(false);
  const [amendedIntent, setAmendedIntent] = useState(storedAmendment);

  const { control, handleSubmit, setValue } = useForm<IntentForm>({
    defaultValues: { intent: '' },
    resolver: yupResolver(intentFormSchema) as Resolver<IntentForm>,
  });

  const selectedIntent = useWatch({ control, name: 'intent' });
  const existingIntent = brainIntents?.find((i) => i.intent === selectedIntent);
  const trainingPhraseExists = Boolean(existingIntent?.expressions?.includes(userMessage));

  const trackPhraseAdded = () =>
    trackEvent(EventName.TrainingPhraseAdded, {
      session_id: payload.session_id,
      brain_id: brainId,
    });

  const onSubmit = async (data: IntentForm) => {
    const intentName = data.intent;

    if (!existingIntent) {
      const newIntent: Partial<BrainIntent> = {
        intent: intentName,
        collection: '',
        expressions: [userMessage],
      };
      const result = await createNewIntent(newIntent);
      trackPhraseAdded();
      setShow(false);
      setAmendedIntent(intentName);
      toast.success(t('intent.intent_created', { 0: result?.intent }));
      return;
    }

    try {
      await updateIntentAsync({
        name: existingIntent.intent,
        intent: { expressions: [userMessage, ...existingIntent.expressions] },
      });
      trackPhraseAdded();
      // Only a considered intent can be fed back to the planner as a correction.
      if (candidates.some((i) => i.intent === intentName)) {
        const correction = { ...payload, task: 'planner' as const, correction: intentName };
        const result = await sendCorrection(correction);
        if (result.status === 'ok') {
          dispatch(submitCorrection(correction));
        }
      }
      toast.success(t('try_it.amendment_submitted'));
      setShow(false);
      setAmendedIntent(intentName);
    } catch {
      // useIntents and useSendCorrection surface errors via toast.
    }
  };

  const consideredOptions = candidates
    .filter((intent) => intent.intent !== 'offtopics')
    .map((intent) => ({
      label: intent.intent,
      value: intent.intent,
      category: t('try_it.considered_intents'),
    }));

  const otherOptions = (brainIntents ?? [])
    .filter(
      (intent) =>
        !candidates.some((c) => c.intent === intent.intent) && intent.intent !== 'offtopics'
    )
    .map((intent) => ({
      label: intent.intent,
      value: intent.intent,
      category: t('try_it.other_intents'),
    }));

  const handleEdit = () => {
    setValue('intent', amendedIntent ?? '');
    setAmendedIntent(undefined);
    setShow(true);
  };

  if (amendedIntent) {
    return <AmendedLabel onEdit={handleEdit} />;
  }

  const handleAmendClick = () => {
    setShow((prev) => !prev);
    trackEvent(EventName.ToggleTrainingPhrasePanel, { session_id: payload.session_id });
  };

  return (
    <div className="w-full self-stretch">
      {!show && (
        <button
          type="button"
          className={AMEND_BUTTON_CLASS}
          onClick={handleAmendClick}
          disabled={!canWrite}
        >
          <PencilIcon size={16} />
          {t('try_it.amend')}
        </button>
      )}

      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          show ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div
          className={cn(
            'flex flex-col items-end gap-4 self-stretch',
            show ? 'overflow-visible py-2' : 'overflow-hidden'
          )}
        >
          <div className="flex flex-col items-start gap-2 self-stretch p-0">
            <FormAutocomplete
              control={control}
              options={[...consideredOptions, ...otherOptions]}
              name="intent"
              placeholder={t('try_it.select_intent_placeholder')}
            />
            <span className="text-sm leading-5 font-normal text-foreground-muted">
              {t('try_it.message_phrase_hint')}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost-neutral" size="sm" onClick={() => setShow(false)}>
              {t('common.cancel')}
            </Button>
            <InfoTooltip
              content={trainingPhraseExists ? t('training_phrases.error') : ''}
              open={trainingPhraseExists ? undefined : false}
              showArrow
            >
              <span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit(onSubmit)}
                  disabled={trainingPhraseExists}
                  isLoading={isAmending || updateStatus === 'pending'}
                >
                  {t('common.submit')}
                </Button>
              </span>
            </InfoTooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export const IntentAmend = ({ turn }: { turn: ConversationTurn }) => {
  const payload = useAmendPayload(turn, 'planner');

  if (!payload) {
    return null;
  }

  return <IntentAmendControl key={payload.request_id} turn={turn} payload={payload} />;
};

/** True when the turn should offer the intent amend (legacy `showAddTrainingPhrase`). */
export const shouldShowIntentAmend = (turn: ConversationTurn) =>
  getIntentInfo(turn).hasInfo && turn.metadata.intent?.confidence !== 1;

/** The intent timeline row, shared by the Workflow, Guidelines/LLM and MCP variants. */
export const intentRow = (turn: ConversationTurn): DetailRowSpec => {
  const info = getIntentInfo(turn);
  return {
    key: 'intent',
    icon: <LocateFixedIcon className="h-4 w-4" />,
    head: (
      <IntentDetails
        info={info}
        amend={shouldShowIntentAmend(turn) && <IntentAmend turn={turn} />}
      />
    ),
  };
};
